/**
 * Whether a registration (holding) can back a new claim, checked as early as
 * Select Registration's "Next" (WPS-154) so the agent is told before typing
 * any claim details.
 *
 * Mirrors the backend's lifecycle guard in ``create_claim``
 * (``apps/claims/services/claims.py``): only a ``registered`` holding can be
 * claimed against. The backend check stays the source of truth and still
 * refuses anything that skips this step (deep links, stale drafts); this only
 * moves the same message forward. The wording matches the backend refusal.
 */

export const CLAIMABLE_HOLDING_STATUS = 'registered'

export type IneligibleRegistrationMessage = {
  key: string
  defaultValue: string
  params?: Record<string, string>
}

const REASONS: Record<string, IneligibleRegistrationMessage> = {
  draft: {
    key: 'submitClaim.ineligibleRegistration.draft',
    defaultValue:
      "Cannot raise a claim against this registration (status 'Draft'). " +
      'It has not been activated yet, so it has no {{warrantyNounLower}} to claim against.',
  },
  cancelled: {
    key: 'submitClaim.ineligibleRegistration.cancelled',
    defaultValue:
      "Cannot raise a claim against this registration (status 'Cancelled'). " +
      'It has been cancelled.',
  },
  transferred: {
    key: 'submitClaim.ineligibleRegistration.transferred',
    defaultValue:
      "Cannot raise a claim against this registration (status 'Transferred'). " +
      'It has been transferred - raise the claim against the registration ' +
      'created for the new owner.',
  },
}

/**
 * The refusal to show for a picked registration, or ``null`` when it can be
 * claimed against. An unknown status (none was captured, e.g. a draft resumed
 * from an older build) is not blocked here - the backend still decides.
 */
export function ineligibleRegistrationMessage(
  status: string | null | undefined,
): IneligibleRegistrationMessage | null {
  if (!status || status === CLAIMABLE_HOLDING_STATUS) return null
  return (
    REASONS[status] ?? {
      key: 'submitClaim.ineligibleRegistration.other',
      defaultValue:
        "Cannot raise a claim against this registration (status '{{status}}').",
      params: { status },
    }
  )
}
